/**
 * Serviço de API - Integração com Backend
 */

import { LeadGroup, Lead, FollowUpEntry } from '../store/slices/groupsSlice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Interface para dados do usuário
export interface SyncUserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  whatsapp?: string;
}

// Helper para fazer requisições
async function fetchAPI(endpoint: string, options?: RequestInit) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Erro ${response.status}`);
  }

  return response.json();
}

export const api = {
  // Health Check
  health: {
    check: () => fetchAPI('/health'),
  },

  // Autenticação
  auth: {
    syncUser: (userData: SyncUserData) => {
      return fetchAPI('/auth/sync', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    },
    getUser: (uid: string) => {
      return fetchAPI(`/auth/user/${uid}`);
    },
  },

  // Grupos
  groups: {
    list: async (userId?: string, isAdmin?: boolean): Promise<LeadGroup[]> => {
      const queryParams = new URLSearchParams();
      if (userId) queryParams.append('userId', userId);
      if (isAdmin !== undefined) queryParams.append('isAdmin', String(isAdmin));
      
      const groups = await fetchAPI(`/groups?${queryParams.toString()}`);
      return groups.map((g: any) => ({
        id: g.id,
        name: g.name,
        status: g.status,
        userId: g.userId,
        leads: [],
        stats: {
          total: g.totalLeads || 0,
          contactado: g.contactado || 0,
          recusou: g.recusou || 0,
          semContato: g.semContato || 0,
          emContato: g.emContato || 0,
          fechado: g.fechado || 0,
        },
        createdAt: g.createdAt,
      }));
    },

    create: async (group: { id: string; name: string; status: string; leads: any[]; userId?: string }) => {
      return fetchAPI('/groups', {
        method: 'POST',
        body: JSON.stringify(group),
      });
    },

    update: (id: string, data: { name?: string; status?: string }) => {
      return fetchAPI(`/groups/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    updateStatus: (id: string, status: string) => {
      return fetchAPI(`/groups/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
    },

    delete: (id: string) => {
      return fetchAPI(`/groups/${id}`, {
        method: 'DELETE',
      });
    },

    getStats: (id: string) => {
      return fetchAPI(`/groups/${id}/stats`);
    },
  },

  // Leads
  leads: {
    list: async (groupId?: string): Promise<Lead[]> => {
      const query = groupId ? `?groupId=${groupId}` : '';
      const leads = await fetchAPI(`/leads${query}`);
      
      return leads.map((l: any) => ({
        id: l.id,
        name: l.name,
        address: l.address,
        phone: l.phone,
        whatsapp: l.whatsapp,
        email: l.email,
        website: l.website,
        coordinates: {
          lat: l.lat,
          lng: l.lng,
        },
        leadStatus: l.status,
        followUps: [],
      }));
    },

    create: (lead: any) => {
      return fetchAPI('/leads', {
        method: 'POST',
        body: JSON.stringify(lead),
      });
    },

    createBulk: (groupId: string, leads: any[]) => {
      return fetchAPI('/leads/bulk', {
        method: 'POST',
        body: JSON.stringify({ groupId, leads }),
      });
    },

    update: (id: string, data: any) => {
      return fetchAPI(`/leads/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    updateStatus: (id: string, status: string) => {
      return fetchAPI(`/leads/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
    },

    delete: (id: string) => {
      return fetchAPI(`/leads/${id}`, {
        method: 'DELETE',
      });
    },

    addFollowUp: (leadId: string, followUp: { type: string; notes: string }) => {
      return fetchAPI(`/leads/${leadId}/followup`, {
        method: 'POST',
        body: JSON.stringify(followUp),
      });
    },

    getFollowUps: async (leadId: string): Promise<FollowUpEntry[]> => {
      const followUps = await fetchAPI(`/leads/${leadId}/followups`);
      return followUps.map((f: any) => ({
        id: f.id,
        date: f.createdAt,
        type: f.type,
        notes: f.notes,
      }));
    },
  },
};

export default api;
